"use client";

/**
 * Shared markdown renderers for skill surfaces (the /skills/[id] page AND the
 * propose_skill draft card), so a drafted skill reads EXACTLY like a saved one.
 *
 * `MD_COMPONENTS` = full body (headings, lists, code, emerald-bold accents).
 * `DESCRIPTION_MD_COMPONENTS` = the tight one-line subtitle renderer.
 *
 * Style: white body text, emerald accents, near-black code blocks. NO gray.
 * (.cursor/rules/xynth-frontend-copy-and-styling.mdc)
 */

import type React from "react";
import { cn } from "@/lib/utils";

export const MD_COMPONENTS = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="mb-2.5 mt-8 text-2xl font-bold tracking-tight text-white first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="mb-2 mt-7 text-lg font-bold tracking-tight text-white">
      {children}
    </h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="mb-1.5 mt-6 text-[12px] font-bold uppercase tracking-widest text-white">
      {children}
    </h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-3.5 leading-[1.6] text-white">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-3.5 list-none space-y-1.5 pl-0">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="mb-3.5 list-decimal space-y-1.5 pl-5 marker:text-white/85">
      {children}
    </ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="relative pl-4 text-white before:absolute before:left-0 before:top-[0.6em] before:size-1.5 before:rounded-full before:bg-emerald-400/70">
      {children}
    </li>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-emerald-300">{children}</strong>
  ),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  code: ({ className, children, ...props }: any) => {
    const isInline = !className?.includes("language-");
    return isInline ? (
      <code
        className="rounded-md bg-emerald-500/10 px-1.5 py-0.5 font-mono text-[13px] text-emerald-400 ring-1 ring-inset ring-emerald-500/20"
        {...props}
      >
        {children}
      </code>
    ) : (
      <code className={cn("font-mono text-[13px] text-white", className)} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="mb-4 overflow-x-auto rounded-lg bg-[#050505] p-5 ring-1 ring-inset ring-white/[0.06]">
      {children}
    </pre>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="my-6 border-l-2 border-emerald-400/30 bg-emerald-500/[0.02] py-4 pl-5 pr-4 italic text-white">
      {children}
    </blockquote>
  ),
};

/** Compact renderer for the one-line subtitle: no paragraph margins, same
 *  emerald bold accent so the key phrases pop. */
export const DESCRIPTION_MD_COMPONENTS = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="m-0 leading-[1.6]">{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-emerald-300">{children}</strong>
  ),
};
